import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, type QueryKey } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import { isApiError } from './api';
import { hasIndexedDB, kvDel, kvGet, kvSet } from './idb';

/**
 * TanStack Query setup with the cache persisted to IndexedDB (kv store, key `rq:tts`), so a
 * phone that opens the app on a court with no signal still sees the roster, the shelf and the
 * last scores. The service worker never caches /api; this cache is the offline read path.
 *
 * Query meta (typed below):
 * - `meta: { offline: true }` marks a query prefetch-for-offline. Modules also register a
 *   prefetch with `registerOfflinePrefetch`, which runs after sign-in and whenever we come
 *   back online, so the data is there before the signal goes.
 * - `meta: { persist: false }` keeps a query out of IndexedDB (big or private data).
 */

export interface TtsQueryMeta extends Record<string, unknown> {
  offline?: boolean;
  persist?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: TtsQueryMeta;
  }
}

const DAY = 24 * 60 * 60 * 1000;
export const PERSIST_MAX_AGE = 7 * DAY;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: PERSIST_MAX_AGE,
        networkMode: 'offlineFirst',
        refetchOnWindowFocus: true,
        retry: (count, e) => {
          if (isApiError(e) && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429) return false;
          return count < 2;
        },
      },
      mutations: { networkMode: 'offlineFirst', retry: false },
    },
  });
}

export const queryClient = createQueryClient();

const storage = {
  getItem: async (key: string) => ((await kvGet<string>(`rq:${key}`)) ?? null) as string | null,
  setItem: (key: string, value: string) => kvSet(`rq:${key}`, value),
  removeItem: (key: string) => kvDel(`rq:${key}`),
};

/**
 * What goes to IndexedDB. Successful queries unless they opted out, and never a "not signed in"
 * answer for `me` (key ['auth', 'me']): a phone that signs in and reloads inside the one-second
 * persist throttle would otherwise come back to a restored `null` and show Welcome while its
 * cookie is good. A signed-out phone has nothing to keep for offline anyway.
 */
export function shouldPersistQuery(q: {
  state: { status: string; data: unknown };
  meta?: { persist?: boolean };
  queryKey: readonly unknown[];
}): boolean {
  if (q.state.status !== 'success' || q.meta?.persist === false) return false;
  if (q.queryKey[0] === 'auth' && q.queryKey[1] === 'me' && q.state.data === null) return false;
  return true;
}

export function persistOptions(): Omit<PersistQueryClientOptions, 'queryClient'> {
  const persister = createAsyncStoragePersister({
    storage: hasIndexedDB() ? storage : undefined,
    key: 'tts',
    throttleTime: 1000,
  });
  return {
    persister,
    maxAge: PERSIST_MAX_AGE,
    // A new build drops the old cache, so a changed response shape never haunts us.
    buster: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev',
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
    },
  };
}

type Prefetch = (qc: QueryClient) => Promise<unknown>;
const prefetches = new Map<string, Prefetch>();

/**
 * A module registers what it needs offline (the Lab shelf, the roster, the next run).
 * The function should call `qc.prefetchQuery(...)` with `meta: { offline: true }`.
 */
export function registerOfflinePrefetch(id: string, fn: Prefetch): void {
  prefetches.set(id, fn);
}

/** Runs every registered prefetch. Failures are fine: we try again on the next `online`. */
export async function runOfflinePrefetch(qc: QueryClient = queryClient): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  await Promise.allSettled([...prefetches.values()].map((fn) => fn(qc)));
}

/** Small helper so offline queries look the same everywhere. */
export function offlineQuery<T>(queryKey: QueryKey, queryFn: () => Promise<T>) {
  return { queryKey, queryFn, meta: { offline: true } as TtsQueryMeta, gcTime: PERSIST_MAX_AGE };
}
